import React, { useState, useEffect } from "react";
import { RotateCcw, RotateCw, WandSparkles, Eye } from "lucide-react";
import { isDateOlderThanSevenDays, isFutureDateBlocked } from "../../utils/dateUtils";
import { apiFetch } from "../../../../context/AuthContext";

/**
 * RowActions
 *
 * Renders action buttons for each row in the attendance table:
 * - Reset Check-In
 * - Reset Check-Out
 * - Revert to Original
 * - View History
 *
 * Note: Overtime approval button has been disabled/hidden.
 */
const RowActions = ({
  record,
  theme,
  isRTL,
  t,
  language,
  viewType,
  companyConfigs,
  canResetCheckIn,
  canResetCheckOut,
  canRevertOriginal,
  canViewAttendance,
  // ── overtime approval ──────────────────────────────────────────────────────
  // (Props removed - overtime button is now hidden)
  // ──────────────────────────────────────────────────────────────────────────
  getConfigForEmployee,
  updateAttendancein,
  updateAttendanceout,
  revertAttendanceToOriginal,
  fetchEmployeeHistory = () => { },
  showAlert = () => { },
  BASE_URL = "",
}) => {
  const [resolvedConfig, setResolvedConfig] = useState(null);
  const [configLoading, setConfigLoading] = useState(false);

  const resolvedEmployeeId =
    record.employeeId ||
    record.employee?.employeeId ||
    record.employee?.infinityCode ||
    record.employee?.id ||
    "";

  // Fetch resolved config on mount
  useEffect(() => {
    if (resolvedEmployeeId && record.date && BASE_URL) {
      fetchResolvedConfig();
    }
  }, [resolvedEmployeeId, record.date, BASE_URL, record.clientName, record.workingCompany]);

  const fetchResolvedConfig = async () => {
    try {
      setConfigLoading(true);
      // ✅ FIX: clientName and companyName are two distinct identifiers
      // (companyConfigService looks configs up by the composite key
      // {clientName, companyName}). The previous code sent
      // `record.workingCompany` in as `clientName`, and never sent
      // `companyName` at all — so for any client with more than one
      // company (the normal case), the backend could not find the
      // matching configuration and silently fell back to a default /
      // wrong company's config. Both fields are already present on the
      // attendance record (see attendanceService.js, which returns
      // `clientName` and `workingCompany` on every record), so send
      // them separately instead of conflating the two.
      const queryParams = new URLSearchParams({
        date: record.date,
        clientName: record.clientName || '',
        companyName: record.workingCompany || '',
      });

      const response = await apiFetch(
        `${BASE_URL}/attendance/${resolvedEmployeeId}/resolved-config?${queryParams}`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            'Accept-Language': language,
          },
        }
      );

      if (response.ok) {
        const data = await response.json();
        setResolvedConfig(data.config || null);
      } else {
        console.warn('❌ Failed to fetch resolved config');
        setResolvedConfig(null);
      }
    } catch (error) {
      console.error('❌ Error fetching resolved config:', error);
      setResolvedConfig(null);
    } finally {
      setConfigLoading(false);
    }
  };

  const getDisplayTime = (type) => {
    if (!resolvedConfig) {
      const baseConfig = getConfigForEmployee(
        record.workingCompany,
        record.employeeType,
        record.date
      );
      return type === 'in' ? (baseConfig?.workStartTime) : (baseConfig?.workEndTime);
    }
    return type === 'in' ? (resolvedConfig.workStartTime) : (resolvedConfig.workEndTime);
  };

  // ── Future-date rule ──────────────────────────────────────────────────────
  // A day after today has not happened yet: no reset, no revert, no overtime
  // action. Unlike the 7-day rule this applies to EVERY view, admin included,
  // because the backend refuses those requests outright (400,
  // code "FUTURE_DATE_NOT_ALLOWED").
  //
  // isFutureDateBlocked (not isFutureDate) so the ALLOW_FUTURE_DATES testing
  // flag can leave these buttons live on future-dated rows — the backend
  // still refuses, which is the point of the flag.
  const isFutureRecord = isFutureDateBlocked(record.date);

  const isAdminView = viewType === "admin";
  const isManagerView = viewType === "manager";
  const hasManualEdits = Boolean(record?.manuallyEdited || record?.editedByIn || record?.editedByOut);
  // The can* props are already resolved upstream (feature grant in Admin View,
  // allow-all in manager view, role bypass for admins) — don't re-OR them with
  // the view type here or admin-level gating is defeated.
  const canShowRevert = canRevertOriginal && hasManualEdits && !isFutureRecord;
  const canShowHistory = canViewAttendance;
  const isTooOldForManager = !isAdminView && isDateOlderThanSevenDays(record.date);

  // Per-record overtime approval required check - removed for now since overtime button is hidden

  // The OT button renders for admin, manager, and employee (employee gets read-only).
  // We only show it when:
  //   1. The company config requires approval for this record
  //   2. The record actually has overtime minutes
  // (This logic is kept for reference but the button is hidden)

  return (
    <div
      style={{
        display: "flex",
        gap: "6px",
        flexDirection: isRTL ? "row-reverse" : "row",
        justifyContent: "center",
        flexWrap: "nowrap",
        alignItems: "center",
      }}
    >
      {/* ── Reset Check-In ────────────────────────────────────────────────── */}
      {canResetCheckIn && (() => {
        const isTooOld = !isAdminView && isDateOlderThanSevenDays(record.date);
        const isDisabled =
          Object.keys(companyConfigs).length === 0 || isTooOld || isFutureRecord;
        const disabledReason = isFutureRecord
          ? t.futureDateTooltip
          : isTooOld
            ? t.dateTooOldTooltip
            : "";
        const checkInTime = getDisplayTime('in');
        const scheduleBadge ="";
        return (
          <button
            type="button"
            onClick={() => updateAttendancein(record.id, record)}
            title={
              isDisabled
                ? disabledReason
                : `${t.resetCheckIn} (${checkInTime}${scheduleBadge})`
            }
            disabled={isDisabled}
            style={btnStyle({ theme, isDisabled, variant: "neutral" })}
            onMouseEnter={(e) => hoverIn(e, { theme, isDisabled, variant: "neutral" })}
            onMouseLeave={(e) => hoverOut(e, { theme, isDisabled, variant: "neutral" })}
          >
            <RotateCcw size={14} />
          </button>
        );
      })()}

      {/* ── Reset Check-Out ───────────────────────────────────────────────── */}
      {canResetCheckOut && (() => {
        const isTooOld = !isAdminView && isDateOlderThanSevenDays(record.date);
        const isDisabled =
          Object.keys(companyConfigs).length === 0 || isTooOld || isFutureRecord;
        const disabledReason = isFutureRecord
          ? t.futureDateTooltip
          : isTooOld
            ? t.dateTooOldTooltip
            : "";
        const checkOutTime = getDisplayTime('out');
        const scheduleBadge = resolvedConfig ? (language === "ar" ? " (📍 جدول الموقع)" : " (📍 Location Schedule)") : "";
        return (
          <button
            type="button"
            onClick={() => updateAttendanceout(record.id, record)}
            title={
              isDisabled
                ? disabledReason
                : `${t.resetCheckOut} (${checkOutTime}${scheduleBadge})`
            }
            disabled={isDisabled}
            style={btnStyle({ theme, isDisabled, variant: "neutral" })}
            onMouseEnter={(e) => hoverIn(e, { theme, isDisabled, variant: "neutral" })}
            onMouseLeave={(e) => hoverOut(e, { theme, isDisabled, variant: "neutral" })}
          >
            <RotateCw size={14} />
          </button>
        );
      })()}

      {/* ── Revert to Original ───────────────────────────────────────────── */}
      {canShowRevert &&
        !isTooOldForManager && (
          <button
            type="button"
            onClick={() => {
              showAlert(
                language === "ar" ? "تأكيد الاستعادة" : "Confirm Revert",
                language === "ar"
                  ? "هل تريد استعادة البيانات الأصلية من جهاز البصمة؟ سيتم حذف التعديلات اليدوية."
                  : "Revert to original fingerprint data? Manual edits will be removed.",
                "warning",
                () => revertAttendanceToOriginal(record),
                true
              );
            }}
            title={language === "ar" ? "استعادة البيانات الأصلية" : "Revert to original"}
            style={btnStyle({ theme, isDisabled: false, variant: "danger" })}
            onMouseEnter={(e) => hoverIn(e, { theme, isDisabled: false, variant: "danger" })}
            onMouseLeave={(e) => hoverOut(e, { theme, isDisabled: false, variant: "danger" })}
          >
            <WandSparkles size={14} />
          </button>
        )}

      {/* ── View Attendance History ───────────────────────────────────────── */}
      {canShowHistory && (
        <button
          type="button"
          onClick={() => fetchEmployeeHistory(resolvedEmployeeId)}
          title={t.viewHistory}
          style={btnStyle({ theme, isDisabled: false, variant: "neutral" })}
          onMouseEnter={(e) => hoverIn(e, { theme, isDisabled: false, variant: "neutral" })}
          onMouseLeave={(e) => hoverOut(e, { theme, isDisabled: false, variant: "neutral" })}
        >
          <Eye size={14} />
        </button>
      )}
    </div>
  );
};

// ── style helpers ──────────────────────────────────────────────────────────────
function btnStyle({ theme, isDisabled, variant }) {
  const isDark = theme === "dark";

  const variants = {
    neutral: {
      bg: isDisabled
        ? (isDark ? "rgba(51,65,85,0.5)" : "rgba(241,245,249,0.7)")
        : (isDark ? "rgba(71,85,105,0.6)" : "rgba(226,232,240,0.8)"),
      color: isDisabled
        ? (isDark ? "rgba(255,255,255,0.5)" : "rgba(0,0,0,0.5)")
        : (isDark ? "#f1f5f9" : "#334155"),
    },
    danger: {
      bg: isDark ? "rgba(239,68,68,0.25)" : "rgba(254,226,226,0.9)",
      color: isDark ? "#fca5a5" : "#b91c1c",
    },
  };

  const v = variants[variant] || variants.neutral;

  return {
    padding: "8px 12px",
    borderRadius: "8px",
    border: "none",
    background: v.bg,
    color: v.color,
    cursor: isDisabled ? "not-allowed" : "pointer",
    opacity: isDisabled ? 0.6 : 1,
    fontSize: "12px",
    fontWeight: "600",
    display: "flex",
    alignItems: "center",
    gap: "6px",
    transition: "all 0.2s ease",
  };
}

function hoverIn(e, { theme, isDisabled, variant }) {
  if (isDisabled) return;
  const isDark = theme === "dark";
  if (variant === "danger") {
    e.currentTarget.style.background = isDark ? "rgba(239,68,68,0.4)" : "rgba(254,202,202,0.9)";
  } else {
    e.currentTarget.style.background = isDark ? "rgba(71,85,105,0.8)" : "rgba(203,213,225,0.8)";
  }
  e.currentTarget.style.transform = "translateY(-2px)";
}

function hoverOut(e, { theme, isDisabled, variant }) {
  const isDark = theme === "dark";
  if (variant === "danger") {
    e.currentTarget.style.background = isDark ? "rgba(239,68,68,0.25)" : "rgba(254,226,226,0.9)";
  } else {
    e.currentTarget.style.background = isDisabled
      ? (isDark ? "rgba(51,65,85,0.5)" : "rgba(241,245,249,0.7)")
      : (isDark ? "rgba(71,85,105,0.6)" : "rgba(226,232,240,0.8)");
  }
  e.currentTarget.style.transform = "translateY(0)";
}

export default RowActions;